import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Session } from "@supabase/supabase-js";

import { supabase } from "../lib/supabase";
import { ApiError, fetchJson, TENANT_ACCESS_CHANGED_EVENT } from "../lib/api";
import { authErrorMessage } from "../lib/authErrorLabels";
import { DEMO_USER, isDemoMode, isLegacyAuthMode } from "../lib/env";

export type TenantRole = "admin" | "member";

export type ActiveTenantMembership = {
  tenant_slug: string;
  tenant_name: string;
  role: TenantRole;
  can_manage_members: boolean;
  can_manage_sources: boolean;
  can_manage_data_quality: boolean;
  can_use_legacy_identity_review: boolean;
  /** What the navigation may show (#538); every route keeps its own gate. */
  can_review_prices: boolean;
  can_manage_cost_centers: boolean;
  can_manage_imports: boolean;
  can_manage_purchase_selections: boolean;
  /** Beisteuern und Freigeben sind eigene Entscheidungen (#557). */
  can_upload_documents: boolean;
  can_approve_order_confirmations: boolean;
  fiscal_year_start_month: number;
};

export type TenantMembership = Omit<
  ActiveTenantMembership,
  | "can_manage_members"
  | "can_manage_sources"
  | "can_manage_data_quality"
  | "can_use_legacy_identity_review"
  | "can_review_prices"
  | "can_manage_cost_centers"
  | "can_manage_imports"
  | "can_manage_purchase_selections"
  | "can_upload_documents"
  | "can_approve_order_confirmations"
  | "fiscal_year_start_month"
> & {
  is_active: boolean;
};

export type SessionUser = {
  user_id: string;
  email: string | null;
  customer_id: string;
  active_membership: ActiveTenantMembership;
  memberships: TenantMembership[];
};

export type LoginPayload = {
  email: string;
  password: string;
};

export const SESSION_QUERY_KEY = ["auth", "me"] as const;

// Set VITE_SUPABASE_DISABLED=true in .env.local only for legacy session-cookie
// smoke or diagnostics. Default beta rehearsal uses Supabase Auth.
const LEGACY_AUTH = isLegacyAuthMode();

// Demo-Mode: kein echter Login/Supabase, synthetischer Nutzer.
const DEMO = isDemoMode();
// Beide Pfade umgehen den Supabase-Auth-Listener.
const AUTH_BYPASS = LEGACY_AUTH || DEMO;

async function fetchProfile(session: Session | null | undefined): Promise<SessionUser | null> {
  if (DEMO) {
    return {
      ...DEMO_USER,
      active_membership: { ...DEMO_USER.active_membership },
      memberships: DEMO_USER.memberships.map((membership) => ({ ...membership })),
    };
  }
  if (LEGACY_AUTH) {
    try {
      return parseSessionUser(await fetchJson<unknown>("/api/auth/me"));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  }

  if (!session) {
    return null;
  }
  const profile = parseSessionUser(await fetchJson<unknown>("/api/auth/profile"));
  if (profile.user_id !== session.user.id) {
    throw new Error("Das Zugriffsprofil gehört nicht zur angemeldeten Person.");
  }
  return {
    ...profile,
    email: profile.email ?? session.user.email ?? null,
  };
}

export const SessionContext = createContext<ReturnType<typeof useSessionState> | null>(null);

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) throw new Error("SessionProvider missing");
  return context;
}

// One owner for auth events and the profile query, outside the protected data cache.
export function useSessionState() {
  const queryClient = useQueryClient();
  const authRevision = useRef(0);
  const authSubject = useRef<string | undefined>(undefined);
  const [session, setSession] = useState<Session | null | undefined>(
    AUTH_BYPASS ? null : undefined,
  );
  const [sessionError, setSessionError] = useState<Error | null>(null);
  // Only a server signal that access changed hides protected content while the
  // profile is rechecked. Routine rechecks of the same identity stay invisible.
  const pendingAccessRechecks = useRef(0);
  const [accessRecheckPending, setAccessRecheckPending] = useState(false);

  const refreshSupabaseSession = useCallback(async (): Promise<Session | null> => {
    const revision = authRevision.current;
    const { data, error } = await supabase.auth.getSession();
    if (revision !== authRevision.current) return data.session;
    if (error) {
      const normalized = new Error(
        authErrorMessage(
          error,
          "Die Anmeldung konnte nicht geprüft werden. Bitte erneut versuchen.",
        ),
      );
      setSessionError(normalized);
      throw normalized;
    }
    setSessionError(null);
    authSubject.current = data.session?.user.id;
    setSession(data.session);
    return data.session;
  }, []);

  useEffect(() => {
    if (AUTH_BYPASS) return;

    void refreshSupabaseSession().catch(() => undefined);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      authRevision.current += 1;
      setSessionError(null);
      const changed = authSubject.current !== s?.user.id;
      authSubject.current = s?.user.id;
      if (changed) queryClient.removeQueries({ queryKey: SESSION_QUERY_KEY });
      else void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      setSession(s);
    });

    return () => subscription.unsubscribe();
  }, [queryClient, refreshSupabaseSession]);

  useEffect(() => {
    const recheckTenantAccess = () => {
      pendingAccessRechecks.current += 1;
      setAccessRecheckPending(true);
      void queryClient
        .refetchQueries({ queryKey: SESSION_QUERY_KEY })
        .finally(() => {
          pendingAccessRechecks.current -= 1;
          if (pendingAccessRechecks.current === 0) setAccessRecheckPending(false);
        });
    };
    window.addEventListener(TENANT_ACCESS_CHANGED_EVENT, recheckTenantAccess);
    return () => window.removeEventListener(TENANT_ACCESS_CHANGED_EVENT, recheckTenantAccess);
  }, [queryClient]);

  const sessionIdentity = AUTH_BYPASS
    ? "bypass"
    : session?.user.id ?? "anonymous";

  const sessionQuery = useQuery({
    queryKey: [...SESSION_QUERY_KEY, sessionIdentity],
    queryFn: () => fetchProfile(session),
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: "always",
    retryOnMount: false,
    retry: (failureCount, error) => {
      if (
        error instanceof ApiError &&
        (error.status === 403 || error.status === 423)
      ) {
        return false;
      }
      return failureCount < 1;
    },
    enabled: AUTH_BYPASS ? true : session !== undefined && !sessionError,
  });

  const retrySession = useCallback(async () => {
    if (!AUTH_BYPASS) {
      try {
        const refreshed = await refreshSupabaseSession();
        if (refreshed?.user.id !== session?.user.id) return;
      } catch {
        return;
      }
    }
    await sessionQuery.refetch();
  }, [refreshSupabaseSession, session?.user.id, sessionQuery]);

  const loginMutation = useMutation({
    mutationFn: async (payload: LoginPayload) => {
      if (DEMO) {
        return null;
      }
      if (LEGACY_AUTH) {
        return fetchJson("/api/auth/login", {
          method: "POST",
          body: JSON.stringify({ username: payload.email, customer_id: "neubauer" }),
        });
      }
      const { data, error } = await supabase.auth.signInWithPassword({
        email: payload.email,
        password: payload.password,
      });
      if (error) {
        // Englische Roh-Meldung bleibt für Diagnose in der Konsole.
        console.error("Supabase signIn error", error);
        throw new Error(authErrorMessage(error));
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      if (DEMO) {
        return;
      }
      if (LEGACY_AUTH) {
        await fetchJson("/api/auth/logout", { method: "POST" });
        return;
      }
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Supabase signOut error", error);
        throw new Error(
          authErrorMessage(error, "Abmeldung fehlgeschlagen. Bitte erneut versuchen."),
        );
      }
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: SESSION_QUERY_KEY });
      void queryClient.invalidateQueries();
    },
  });

  const profileError = sessionError ?? sessionQuery.error;
  const profileIsError = profileError !== null;
  const sessionIsLoading =
    (!AUTH_BYPASS && session === undefined && !sessionError) ||
    sessionQuery.isLoading;

  return {
    user: profileIsError ? null : sessionQuery.data ?? null,
    isLoading: sessionIsLoading,
    isFetching: sessionIsLoading || sessionQuery.isFetching,
    accessRecheckPending,
    isError: profileIsError,
    error: profileError,
    retrySession,
    session,
    login: loginMutation.mutateAsync,
    loginPending: loginMutation.isPending,
    loginError: loginMutation.error,
    logout: logoutMutation.mutateAsync,
    logoutPending: logoutMutation.isPending,
    logoutError: logoutMutation.error,
  };
}

function parseSessionUser(value: unknown): SessionUser {
  if (!isRecord(value)) throw profileContractError();
  const userId = nonEmptyString(value.user_id);
  const customerId = nonEmptyString(value.customer_id);
  const email = value.email;
  const activeMembership = parseActiveMembership(value.active_membership);
  if (!userId || !customerId || (email !== null && typeof email !== "string")) {
    throw profileContractError();
  }
  if (activeMembership.tenant_slug !== customerId) throw profileContractError();
  if (!Array.isArray(value.memberships)) throw profileContractError();

  const memberships = value.memberships.map(parseMembership);
  const activeRows = memberships.filter((membership) => membership.is_active);
  if (activeRows.length !== 1) throw profileContractError();
  const [activeRow] = activeRows;
  if (
    activeRow.tenant_slug !== activeMembership.tenant_slug ||
    activeRow.tenant_name !== activeMembership.tenant_name ||
    activeRow.role !== activeMembership.role
  ) {
    throw profileContractError();
  }

  return {
    user_id: userId,
    email,
    customer_id: customerId,
    active_membership: activeMembership,
    memberships,
  };
}

function parseMembership(value: unknown): TenantMembership {
  if (!isRecord(value) || typeof value.is_active !== "boolean") {
    throw profileContractError();
  }
  const tenantSlug = nonEmptyString(value.tenant_slug);
  const tenantName = nonEmptyString(value.tenant_name);
  const role = value.role;
  if (
    !tenantSlug ||
    !tenantName ||
    (role !== "admin" && role !== "member")
  ) {
    throw profileContractError();
  }
  return {
    tenant_slug: tenantSlug,
    tenant_name: tenantName,
    role,
    is_active: value.is_active,
  };
}

function parseActiveMembership(value: unknown): ActiveTenantMembership {
  if (!isRecord(value)) throw profileContractError();
  const tenantSlug = nonEmptyString(value.tenant_slug);
  const tenantName = nonEmptyString(value.tenant_name);
  const role = value.role;
  if (
    !tenantSlug ||
    !tenantName ||
    (role !== "admin" && role !== "member") ||
    typeof value.can_manage_members !== "boolean" ||
    typeof value.can_manage_sources !== "boolean" ||
    typeof value.can_manage_data_quality !== "boolean" ||
    typeof value.can_use_legacy_identity_review !== "boolean" ||
    typeof value.can_review_prices !== "boolean" ||
    typeof value.can_manage_cost_centers !== "boolean" ||
    typeof value.can_manage_imports !== "boolean" ||
    typeof value.can_manage_purchase_selections !== "boolean" ||
    typeof value.can_upload_documents !== "boolean" ||
    typeof value.can_approve_order_confirmations !== "boolean"
  ) {
    throw profileContractError();
  }
  return {
    tenant_slug: tenantSlug,
    tenant_name: tenantName,
    role,
    can_manage_members: value.can_manage_members,
    can_manage_sources: value.can_manage_sources,
    can_manage_data_quality: value.can_manage_data_quality,
    can_use_legacy_identity_review: value.can_use_legacy_identity_review,
    can_review_prices: value.can_review_prices,
    can_manage_cost_centers: value.can_manage_cost_centers,
    can_manage_imports: value.can_manage_imports,
    can_manage_purchase_selections: value.can_manage_purchase_selections,
    can_upload_documents: value.can_upload_documents,
    can_approve_order_confirmations: value.can_approve_order_confirmations,
    fiscal_year_start_month: fiscalYearStartMonth(
      value.fiscal_year_start_month,
    ),
  };
}

function fiscalYearStartMonth(value: unknown): number {
  if (value === undefined) return 1;
  if (
    typeof value !== "number" ||
    !Number.isInteger(value) ||
    value < 1 ||
    value > 12
  ) {
    throw profileContractError();
  }
  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function profileContractError(): Error {
  return new Error("Das aktive Zugriffsprofil ist unvollständig.");
}
